/**
 * US phone answers (`ui_meta.phone.format === 'us'`, phase U3).
 *
 * The control keeps digits only in form state — that is what the payload,
 * the validator, the draft and the server all see — and formats them for
 * display as (555) 555-5555 while the respondent types. Mirrors
 * server/src/lib/phone.ts; the two must agree. Only this light US grouping
 * exists (decision 2 of the usability playbook): a country picker and
 * international formatting are Tier 3.
 */
import type { FieldDefinition } from '../shared/types';

export const US_PHONE_DIGITS = 10;

/**
 * The digits of whatever was typed or pasted. A leading country code 1 is
 * dropped when it is clearly one ("+1 (555) 010-0000" pasted in), never
 * while the number is still being typed; the rest is capped at ten so the
 * grouping never runs past the last group.
 */
export function usPhoneDigits(raw: unknown): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  const local = digits.length > US_PHONE_DIGITS && digits.startsWith('1') ? digits.slice(1) : digits;
  return local.slice(0, US_PHONE_DIGITS);
}

/** (555) 555-5555, built up group by group so backspacing never gets stuck on a separator. */
export function formatUsPhone(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, US_PHONE_DIGITS);
  if (!d) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Whether a stored answer is a complete US number. */
export const isUsPhone = (digits: string): boolean => digits.length === US_PHONE_DIGITS;

/** Does this field ask for a US-formatted phone number? */
export const wantsUsPhone = (f: Pick<FieldDefinition, 'input_type' | 'ui_meta'>): boolean =>
  f.input_type === 'tel' && f.ui_meta?.phone?.format === 'us';
